import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../../services/api';
import {
  Intent,
  INTENT_LABELS,
  INTENT_SCHOOLS,
  THRESHOLD_SPECS,
  ACTIVATION_MATRIX,
  ThresholdSpec,
  ControlActivation,
  DisciplineThresholds,
  DisciplineThresholdsBundle,
} from '../../../types/discipline';
import DecisionMapPreview from './DecisionMapPreview';

/**
 * DisciplineSection — BAB-61 Phase 3.
 *
 * The Settings → Frameworks → 3 Discipline tabs surface where users tune
 * the six thresholds per intent. Mounts inside `FrameworksSettings.tsx`
 * in place of the deleted v2.2 `PMEConfigurator` section.
 *
 * Architecture:
 *   - One tab per intent (Hold/Wheel/Release with school labels)
 *   - Per-intent CC/CSP toggle (some controls hide on certain combos)
 *   - 6 sliders, each with hard rails (block out-of-range) + soft warnings
 *   - Decision-map preview (BAB-64): engine-driven 2D action grid that
 *     reflows live as the user drags a threshold slider. Strips view kept
 *     as a fallback for slow networks (toggle in the preview column).
 *   - Hydrated from GET /discipline-thresholds (returns all 3 in one call)
 *   - Save = PUT /discipline-thresholds/{intent} per tab
 */

type Strategy = 'cc' | 'csp';
type PreviewView = 'map' | 'strips';

const DisciplineSection: React.FC = () => {
  const [bundle, setBundle] = useState<DisciplineThresholdsBundle | null>(null);
  const [activeIntent, setActiveIntent] = useState<Intent>('hold');
  const [activeStrategy, setActiveStrategy] = useState<Strategy>('cc');
  const [previewView, setPreviewView] = useState<PreviewView>('map');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Load on mount
  useEffect(() => {
    let cancelled = false;
    apiService.getDisciplineThresholds()
      .then((b) => { if (!cancelled) { setBundle(b); setLoading(false); } })
      .catch((e) => {
        if (!cancelled) {
          setErrorMsg('Could not load thresholds');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const activeThresholds = bundle ? bundle[activeIntent] : null;

  const handleChange = (key: keyof DisciplineThresholds, value: number) => {
    if (!bundle) return;
    setBundle({
      ...bundle,
      [activeIntent]: {
        ...bundle[activeIntent],
        [key]: value,
      },
    });
    setSavedMsg(null);
  };

  const handleSave = async () => {
    if (!bundle || !activeThresholds) return;
    setSaving(true);
    setErrorMsg(null);
    setSavedMsg(null);
    try {
      const updated = await apiService.updateDisciplineThresholds(activeIntent, {
        profit_target_pct: activeThresholds.profit_target_pct,
        time_stop_dte: activeThresholds.time_stop_dte,
        defensive_delta: activeThresholds.defensive_delta,
        stock_stop_pct: activeThresholds.stock_stop_pct,
        option_stop_multiple: activeThresholds.option_stop_multiple,
        max_roll_dte_extension: activeThresholds.max_roll_dte_extension,
      });
      setBundle({ ...bundle, [activeIntent]: updated });
      setSavedMsg(`Saved · ${INTENT_LABELS[activeIntent]}`);
      setTimeout(() => setSavedMsg(null), 2500);
    } catch (e) {
      setErrorMsg('Could not save — values may be out of range');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="st-muted-block st-muted-block--left">Loading discipline thresholds…</div>;
  }
  if (!bundle || !activeThresholds) {
    return <div className="st-muted-block st-muted-block--left st-is-loss">{errorMsg || 'No data'}</div>;
  }

  return (
    <div className="st-disc">
      <SectionHeader />

      {/* Intent tabs — school-labeled per founder's locked decision */}
      <IntentTabs activeIntent={activeIntent} onChange={setActiveIntent} />

      {/* CC/CSP toggle — same thresholds apply to both strategies under one intent;
          the toggle just changes which controls are active/inactive per the matrix. */}
      <StrategyToggle activeStrategy={activeStrategy} onChange={setActiveStrategy} />

      <div className="st-disc-grid">
        {/* Thresholds column — reads as a rules table, one row per rule */}
        <div className="st-rules" role="group" aria-label="Discipline thresholds">
          <div className="st-rules-head" aria-hidden="true">
            <span>Rule</span>
            <span>Range</span>
            <span className="st-rules-head-val">Value</span>
          </div>
          {THRESHOLD_SPECS.map((spec) => (
            <ThresholdSlider
              key={spec.key}
              spec={spec}
              value={activeThresholds[spec.key] as number}
              defaultValue={activeThresholds.defaults?.[spec.key] ?? null}
              activation={ACTIVATION_MATRIX[activeIntent]?.[activeStrategy]?.[spec.key] || 'on'}
              onChange={(v) => handleChange(spec.key, v)}
            />
          ))}
        </div>

        {/* Preview column — decision-map (V2 default) with strips fallback */}
        <div className="st-preview-col">
          <PreviewViewToggle view={previewView} onChange={setPreviewView} />
          {previewView === 'map' ? (
            <DecisionMapPreview
              intent={activeIntent}
              strategy={activeStrategy}
              thresholds={activeThresholds}
            />
          ) : (
            <TwoStripsPreview
              thresholds={activeThresholds}
              strategy={activeStrategy}
            />
          )}
        </div>
      </div>

      {/* Save row */}
      <div className="st-save-row">
        <button
          type="button"
          className="st-btn st-btn--primary"
          onClick={handleSave}
          disabled={saving}
          aria-busy={saving}
        >
          {saving ? 'Saving…' : `Save · ${INTENT_LABELS[activeIntent]}`}
        </button>
        {savedMsg && <span className="st-save-msg st-is-gain">✓ {savedMsg}</span>}
        {errorMsg && <span className="st-save-msg st-is-loss">{errorMsg}</span>}
        {activeThresholds.is_user_override && !savedMsg && (
          <span className="st-save-note">
            Customized (overriding spec defaults)
          </span>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Subcomponents
// ============================================================================

const SectionHeader: React.FC = () => (
  <div className="st-sec">
    <h2 className="st-sec-h">Discipline</h2>
    <p className="st-sec-sub">
      Set your discipline. Babylon does the rest. Each tab is a school of thought —
      pick yours and tune the thresholds.
    </p>
  </div>
);

const IntentTabs: React.FC<{ activeIntent: Intent; onChange: (i: Intent) => void }> = ({ activeIntent, onChange }) => (
  <div className="st-intents" role="tablist" aria-label="Intent">
    {(['hold', 'wheel', 'release'] as Intent[]).map((intent) => {
      const isActive = activeIntent === intent;
      return (
        <button
          key={intent}
          type="button"
          role="tab"
          aria-selected={isActive}
          className={`st-intent ${isActive ? 'is-on' : ''}`}
          onClick={() => onChange(intent)}
        >
          {INTENT_LABELS[intent]}
          <span className="st-intent-school">
            {INTENT_SCHOOLS[intent].school}
          </span>
        </button>
      );
    })}
  </div>
);

const PreviewViewToggle: React.FC<{ view: PreviewView; onChange: (v: PreviewView) => void }> = ({ view, onChange }) => (
  <div className="st-ctl-row st-ctl-row--preview">
    <span className="st-ctl-label">View</span>
    <div className="seg">
      {(['map', 'strips'] as PreviewView[]).map((v) => (
        <button
          key={v}
          type="button"
          aria-pressed={view === v}
          onClick={() => onChange(v)}
          title={v === 'map'
            ? 'Engine-driven 2D decision map (what would Discipline do at this price + DTE?)'
            : 'V1 strips — premium decay + stock-price number line'}
        >
          {v === 'map' ? 'Map' : 'Strips'}
        </button>
      ))}
    </div>
  </div>
);

const StrategyToggle: React.FC<{ activeStrategy: Strategy; onChange: (s: Strategy) => void }> = ({ activeStrategy, onChange }) => (
  <div className="st-ctl-row">
    <span className="st-ctl-label">Preview as</span>
    <div className="seg">
      {(['cc', 'csp'] as Strategy[]).map((s) => (
        <button
          key={s}
          type="button"
          aria-pressed={activeStrategy === s}
          onClick={() => onChange(s)}
        >
          {s.toUpperCase()}
        </button>
      ))}
    </div>
  </div>
);

// ============================================================================
// Threshold slider — one row per control
// ============================================================================

interface SliderProps {
  spec: ThresholdSpec;
  value: number;
  /** This intent's default (server); null when unknown. */
  defaultValue: number | null;
  activation: ControlActivation;
  onChange: (v: number) => void;
}

const ThresholdSlider: React.FC<SliderProps> = ({ spec, value, defaultValue, activation, onChange }) => {
  if (activation === 'off') return null;
  const disabled = activation === 'opt-in';
  const [hardMin, hardMax] = spec.hard;
  const soft = spec.soft;
  const inSoftRange = soft ? (value >= soft[0] && value <= soft[1]) : true;
  const isDefault = defaultValue != null && value === defaultValue;
  // Fill position for the neutral slider track (0–100% of the hard rail).
  const span = hardMax - hardMin;
  const fillPct = span > 0 ? Math.max(0, Math.min(100, ((value - hardMin) / span) * 100)) : 0;
  const inputId = `disc-${spec.key}`;

  return (
    <div className={`st-rule ${disabled ? 'is-optin' : ''} ${!inSoftRange ? 'is-soft' : ''}`}>
      <div className="st-rule-name">
        <label htmlFor={inputId} className="st-rule-label">{spec.label}</label>
        {disabled && (
          <span className="st-rule-tag">(opt-in for this intent)</span>
        )}
      </div>

      <div className="st-rule-range">
        <input
          type="range"
          className="st-range"
          min={hardMin}
          max={hardMax}
          step={spec.step}
          value={value}
          disabled={disabled}
          aria-label={`${spec.label} slider`}
          onChange={(e) => onChange(Number(e.target.value))}
          style={{ '--fill': `${fillPct}%` } as React.CSSProperties}
        />
        <div className="st-rule-rails">
          <span>{hardMin}{spec.unit}</span>
          <span>{hardMax}{spec.unit}</span>
        </div>
      </div>

      <div className="st-rule-val">
        <input
          id={inputId}
          type="number"
          className="st-num"
          value={value}
          min={hardMin}
          max={hardMax}
          step={spec.step}
          disabled={disabled}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (v < hardMin || v > hardMax) return;
            onChange(v);
          }}
        />
        <span className="st-unit">{spec.unit}</span>
      </div>

      <div className="st-rule-cite">
        {isDefault && 'Spec default · '}
        {!inSoftRange && soft && (
          <span className="st-is-warn">
            Outside recommended range ({soft[0]}–{soft[1]}{spec.unit}) · {spec.citation}
          </span>
        )}
        {inSoftRange && spec.citation}
      </div>

      {spec.helpText && (
        <div className="st-rule-help">
          {spec.helpText}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Two-strips preview — premium decay + stock price number line
// ============================================================================

const TwoStripsPreview: React.FC<{ thresholds: DisciplineThresholds; strategy: Strategy }> = ({ thresholds, strategy }) => {
  // Worked-example position (spec §2.1 hard-coded so the math is real)
  const example = strategy === 'cc'
    ? { write: 98, strike: 100, credit: 2.50, dte: 45, label: 'CC · $100 strike, 45 DTE, $2.50 credit' }
    : { write: 100, strike: 95, credit: 2.00, dte: 45, label: 'CSP · $95 put, 45 DTE, $2.00 credit' };

  // Premium-decay strip markers
  const profitMarker = thresholds.profit_target_pct;          // % of credit
  const optionStopPct = (thresholds.option_stop_multiple - 1) * 100;  // -X% of credit = loss
  // (option_stop fires when option price reaches multiple × credit, so the strip needs to show
  //  the loss equivalent — for display purposes, render at the "right end" past 100%)

  // Stock-price strip markers (CC only — CSP uses different geometry)
  const stockStopLevel = strategy === 'cc' ? example.write * (1 - thresholds.stock_stop_pct / 100) : null;

  return (
    <div className="st-preview">
      <div className="st-preview-k">Preview · worked example</div>
      <div className="st-preview-ex">{example.label}</div>

      {/* Strip 1: Premium decay */}
      <StripLabel>Premium captured</StripLabel>
      <div className="st-strip">
        {/* Profit target line */}
        <Marker positionPct={profitMarker} color="var(--tk-gain)" label={`Close at ${profitMarker}%`} />
        <Marker positionPct={0} color="var(--tk-t3)" label="0%" hideLabel />
        <Marker positionPct={100} color="var(--tk-t3)" label="100%" hideLabel />
      </div>
      <ScaleRow leftLabel="0%" rightLabel="100% (max profit)" />

      {/* Strip 2: Stock price (CC only — for CSP we show the put-strike geometry) */}
      <div className="st-strip-gap" />
      <StripLabel>{strategy === 'cc' ? 'Stock price' : 'Put strike geometry'}</StripLabel>
      <div className="st-strip">
        {strategy === 'cc' ? (
          <>
            <Marker positionPct={50} color="var(--tk-t3)" label={`Write $${example.write}`} hideLabel />
            <Marker positionPct={50 + ((example.strike - example.write) / example.write) * 100 * 5} color="var(--tk-t1)" label={`Strike $${example.strike}`} />
            {stockStopLevel && (
              <Marker positionPct={50 - thresholds.stock_stop_pct * 5} color="var(--tk-loss)" label={`Stop $${stockStopLevel.toFixed(2)}`} />
            )}
          </>
        ) : (
          <>
            <Marker positionPct={70} color="var(--tk-t3)" label={`Spot $${example.write}`} hideLabel />
            <Marker positionPct={40} color="var(--tk-t1)" label={`Strike $${example.strike}`} />
          </>
        )}
      </div>
      <ScaleRow leftLabel={strategy === 'cc' ? '-20%' : 'OTM'} rightLabel={strategy === 'cc' ? '+20%' : 'ITM'} />

      <div className="st-preview-foot">
        Strips view · shows where your thresholds sit on the premium-decay and
        stock-price scales. Switch to Map view above for the engine-driven 2D
        decision surface.
      </div>
    </div>
  );
};

const StripLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="st-strip-label">{children}</div>
);

const Marker: React.FC<{ positionPct: number; color: string; label: string; hideLabel?: boolean }> = ({ positionPct, color, label, hideLabel }) => {
  const clamped = Math.max(0, Math.min(100, positionPct));
  return (
    <>
      <span className="st-marker" style={{ left: `${clamped}%`, background: color }} />
      {!hideLabel && (
        <span className="st-marker-label" style={{ left: `${clamped}%`, color }}>
          {label}
        </span>
      )}
    </>
  );
};

const ScaleRow: React.FC<{ leftLabel: string; rightLabel: string }> = ({ leftLabel, rightLabel }) => (
  <div className="st-scale">
    <span>{leftLabel}</span>
    <span>{rightLabel}</span>
  </div>
);

export default DisciplineSection;
